/**
 * "Gift for 1 grosz" – keeps the gift line in the cart in sync with the
 * threshold rendered by snippets/cart-gift-progress.liquid.
 *
 * The 1 grosz price is NOT set here: it comes from a Shopify automatic
 * discount (Buy X get Y), so a cart built by hand still pays full price
 * unless the discount conditions are met at checkout.
 *
 * State is read from #CartGift-State, which is re-rendered together with the
 * drawer after every cart change, so a MutationObserver is enough to react to
 * all add/change paths of the theme.
 */
(() => {
  if (window.SupersonicCartGift) return;

  const GIFT_PROPERTY = '_supersonic_gift';
  const DISMISSED_KEY = 'supersonic:cart-gift-dismissed';
  const MAX_AUTO_ACTIONS = 10;

  let busy = false;
  let autoActions = 0;
  let syncTimer;

  const dismissed = {
    get() {
      try {
        return sessionStorage.getItem(DISMISSED_KEY);
      } catch (e) {
        return null;
      }
    },
    set(variantId) {
      try {
        if (variantId) sessionStorage.setItem(DISMISSED_KEY, String(variantId));
        else sessionStorage.removeItem(DISMISSED_KEY);
      } catch (e) {}
    },
  };

  function readState() {
    const el = document.getElementById('CartGift-State');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function nextAction(state) {
    const valid = state.active && state.eligible;
    const lines = state.lines || [];

    // Remove gift lines that should not be there (below threshold, feature off, gift product changed)
    const invalidLine = lines.find((line) => !valid || line.variantId !== state.variantId);
    if (invalidLine) return { endpoint: 'change', body: { id: invalidLine.key, quantity: 0 } };
    if (!valid) return null;

    if (lines.length > 1) return { endpoint: 'change', body: { id: lines[1].key, quantity: 0 } };
    if (lines.length === 1 && lines[0].quantity !== 1) {
      return { endpoint: 'change', body: { id: lines[0].key, quantity: 1 } };
    }
    if (lines.length === 0 && dismissed.get() !== String(state.variantId)) {
      return {
        endpoint: 'add',
        body: { items: [{ id: state.variantId, quantity: 1, properties: { [GIFT_PROPERTY]: '1gr' } }] },
      };
    }
    return null;
  }

  function cartUrl(endpoint) {
    const routes = window.routes || {};
    const base = endpoint === 'add' ? routes.cart_add_url || '/cart/add' : routes.cart_change_url || '/cart/change';
    return `${base}.js`;
  }

  function parse(html, selector) {
    return html ? new DOMParser().parseFromString(html, 'text/html').querySelector(selector) : null;
  }

  function render(response) {
    const sections = response.sections || {};
    const drawer = document.querySelector('cart-drawer');
    const inner = drawer && drawer.querySelector('.drawer__inner');
    const newInner = parse(sections['cart-drawer'], '.drawer__inner');

    if (inner && newInner) {
      const terms = document.getElementById('CartDrawer-Terms');
      const termsChecked = terms && terms.checked;
      inner.innerHTML = newInner.innerHTML;
      const newTerms = document.getElementById('CartDrawer-Terms');
      if (newTerms && termsChecked) newTerms.checked = true;
      if (typeof response.item_count === 'number') drawer.classList.toggle('is-empty', response.item_count === 0);
    }

    const bubble = document.getElementById('cart-icon-bubble');
    const newBubble = parse(sections['cart-icon-bubble'], '.shopify-section');
    if (bubble && newBubble) bubble.innerHTML = newBubble.innerHTML;
  }

  function setPending(pending) {
    const card = document.querySelector('.cart-gift');
    if (card) card.classList.toggle('is-pending', pending);
  }

  function sync() {
    if (busy) return;
    const state = readState();
    if (!state) return;
    const action = nextAction(state);
    if (!action) {
      // Cart converged – the limit only guards against add/remove loops
      autoActions = 0;
      return;
    }
    if (autoActions >= MAX_AUTO_ACTIONS) return;

    busy = true;
    autoActions += 1;
    setPending(true);

    fetch(cartUrl(action.endpoint), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...action.body,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || !data.sections) {
          // e.g. gift out of stock – stop retrying until the next page load
          autoActions = MAX_AUTO_ACTIONS;
          setPending(false);
          return;
        }
        render(data);
      })
      .catch(() => {
        autoActions = MAX_AUTO_ACTIONS;
        setPending(false);
      })
      .finally(() => {
        busy = false;
      });
  }

  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(sync, 150);
  }

  function init() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    // Customer removed the gift on purpose – don't add it back automatically this session
    document.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('.cart-item--gift cart-remove-button')) {
          const state = readState();
          if (state) dismissed.set(state.variantId);
        }
      },
      true
    );

    drawer.addEventListener('click', (event) => {
      if (!event.target.closest('[data-cart-gift-add]')) return;
      event.preventDefault();
      dismissed.set(null);
      autoActions = 0;
      sync();
    });

    new MutationObserver(scheduleSync).observe(drawer, { childList: true, subtree: true });
    scheduleSync();
  }

  window.SupersonicCartGift = { sync: scheduleSync };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
